import { useCallback, useEffect, useMemo, useState } from 'react'

import { useForm } from 'react-hook-form'

import { useAppRouter as useRouter } from '@/shared/hooks/useAppRouter'
import { useModalContext } from '@/shared/hooks'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { showErrorAlert } from '@/shared/lib/errors'
import { api } from '@/shared/lib/api'
import { useGamificationDataStore } from '@/shared/store/gamificationDataStore'
import { useMissionsStore } from '@/shared/store/useMissionsStore'
import { alert } from '@/shared/ui/dialog'
import { QRCodeModal } from '@/shared/ui/QRCodeModal'

import { MISSION_SHOW_STATUS_HINTS } from '../constants/showStatus'
import {
  getMissionParticipantsDisplay,
  missionHasParticipantRestriction
} from '../lib/missionParticipantsDisplay'
import {
  canResubmitEvidence,
  canSubmitEvidence,
  getMissionShowStatus
} from '../lib/missionShowStatus'

/**
 * Submissão recusada de missão de evidência por código. Só vale para esse tipo
 * — nos demais, "recusada" pode significar avaliação pendente do produtor, e
 * avisar ali seria mentir sobre o que aconteceu.
 */
const isRefusedCodeSubmission = (data: unknown) => {
  const record = (data as { record?: { status?: string; mission?: { evidence_kind?: string } } })
    ?.record

  return record?.status === 'refused' && record?.mission?.evidence_kind === 'qr_code'
}

export const useMissionShow = (id: string) => {
  const router = useRouter()
  const { qr_code: qrCode } = router.query as { qr_code?: string }
  const { navigateTo } = useNavigation()
  const { showModal } = useModalContext()
  const { refetchProgress, wallet } = useUserProgress()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm({
    defaultValues: { content: '' }
  })

  const {
    mission,
    config: { apiBasePath, humanName, getStatus, getCapacityStatus }
  } = useMissionsStore({ uid: id })

  const gamificationVersion = useGamificationDataStore(
    state => state.getActiveProductData().version
  )
  const isLoading = gamificationVersion === ''

  const timeStatus = getStatus(mission)
  const showStatus = getMissionShowStatus(mission)
  const capacity = mission?._capacity
  const capacityStatusCode = capacity?.status?.code
  const isCapacityFull = capacityStatusCode === 'full'
  const submission = mission?._progress?.submission

  const hasParticipantRestriction = mission
    ? missionHasParticipantRestriction(mission)
    : false
  const totalParticipants = mission
    ? getMissionParticipantsDisplay(mission)
    : null

  const handleCapacityBlocked = useCallback(() => {
    void alert({
      variant: 'info',
      title: 'Missão lotada',
      description:
        'Esta missão atingiu o limite máximo de participantes. Aguarde novas vagas para participar novamente.'
    })
  }, [])

  const handleSubmitContent = useCallback(
    async ({ media_id, content }: { media_id?: number; content?: string }) => {
      if (isSubmitting) return
      if (isCapacityFull) {
        handleCapacityBlocked()
        return
      }

      try {
        setIsSubmitting(true)
        const { data } = await api.post(`${apiBasePath}/${mission?.uid}/finish`, {
          record: { content, media_id }
        })

        // Código errado não é falha de rede: a api aceita a submissão e a marca
        // como recusada. Sem tratar aqui, o campo se limparia em silêncio e o
        // participante ficaria sem saber por que nada aconteceu.
        //
        // O aviso nunca mostra o código certo — quem digita errado tenta de
        // novo, não recebe a resposta de presente. O valor digitado fica no
        // campo, para corrigir em vez de redigitar.
        if (isRefusedCodeSubmission(data)) {
          void alert({
            variant: 'warning',
            title: 'Esse código não confere',
            description:
              'A palavra digitada não corresponde a esta missão. Confira o código e tente de novo.'
          })

          return
        }

        form.reset({ content: '' })
        refetchProgress()
      } catch (error) {
        showErrorAlert(error)
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      apiBasePath,
      form,
      handleCapacityBlocked,
      isCapacityFull,
      isSubmitting,
      mission?.uid,
      refetchProgress
    ]
  )

  const handleOpenCamera = useCallback(() => {
    if (isCapacityFull) {
      handleCapacityBlocked()
      return
    }

    showModal({
      component: QRCodeModal,
      onSave: handleSubmitContent
    })
  }, [handleCapacityBlocked, handleSubmitContent, isCapacityFull, showModal])

  useEffect(() => {
    if (qrCode) handleSubmitContent({ content: qrCode })
  }, [qrCode, handleSubmitContent])

  const showEvidenceForm = useMemo(
    () =>
      canSubmitEvidence(mission, { isCapacityFull }) &&
      !!mission?.confirmation_required,
    [isCapacityFull, mission]
  )

  const showAutoComplete = useMemo(
    () =>
      canSubmitEvidence(mission, { isCapacityFull }) &&
      !mission?.confirmation_required,
    [isCapacityFull, mission]
  )

  const canResubmit = useMemo(
    () => canResubmitEvidence(mission, { isCapacityFull }),
    [isCapacityFull, mission]
  )

  const isReadonly = showStatus === 'expired' || showStatus === 'not_started'

  const showTimeline =
    showStatus !== 'not_started' && !!mission?.confirmation_required

  return {
    mission,
    isLoading,
    humanName,
    timeStatus,
    showStatus,
    statusHint: MISSION_SHOW_STATUS_HINTS[showStatus],
    showTimeline,
    canResubmit,
    capacity,
    capacityStatus: getCapacityStatus(capacity?.status?.code),
    hasParticipantRestriction,
    totalParticipants,
    isCapacityFull,
    submission,
    wallet,
    form,
    isSubmitting,
    isReadonly,
    showEvidenceForm,
    showAutoComplete,
    handleSubmitContent,
    handleOpenCamera,
    navigateBack: () => navigateTo('/missoes')
  }
}
